import React from "react";
import classNames from "classnames";

// Shared card wrapper so pages keep the same width/box while their content
// swaps between waiting, form, receipt and error states.
export const Card = ({ children }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">{children}</div>
    </div>
);

export const formatDepartureTime = value => {
    if (!value) {
        return null;
    }
    // Pivot value can be "HH:MM:SS" or "YYYY-MM-DD HH:MM:SS".
    const match = String(value).match(/(\d{4}-\d{2}-\d{2})?[ T]?(\d{2}:\d{2})/);
    if (!match) {
        return String(value);
    }
    return match[1] ? `${match[1]} kl ${match[2]}` : `kl ${match[2]}`;
};

export const TicketList = ({ tickets }) => (
    <div>
        {tickets.map((ticket, index) => (
            <div key={index} className={classNames("bg-white shadow overflow-hidden sm:rounded-lg", { "mt-8": index !== 0 })}>
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">{ticket.passengerName}</h3>
                </div>
                <div className="border-t border-gray-200">
                    <dl>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Från</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{ticket.departureStation}</dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Till</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{ticket.arrivalStation}</dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Standard</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{ticket.firstClass ? 'Första klass' : 'Andra klass'}</dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Pris</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{ticket.price}</dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Vagn</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{ticket.carriage}</dd>
                        </div>
                        <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Plats</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{ticket.seat}</dd>
                        </div>
                        <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-500">Bokningsnummer</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{ticket.bookingNo}</dd>
                        </div>
                    </dl>
                </div>
            </div>
        ))}
    </div>
);

// Receipt for a completed booking: summary, mail confirmation and tickets.
const BookingReceipt = ({ booking, heading }) => (
    <div>
        <h3 className="text-center text-base font-semibold text-indigo-600 tracking-wide uppercase">{heading || 'Din bokning'}</h3>
        <dl className="mt-6 border border-gray-200 rounded-md divide-y divide-gray-200">
            <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Bokningsnummer</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{booking.bookingNo}</dd>
            </div>
            {booking.trainName && (
                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="text-sm font-medium text-gray-500">Avgång</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {booking.trainName}
                        {formatDepartureTime(booking.departureTime) ? ` ${formatDepartureTime(booking.departureTime)}` : ''}
                    </dd>
                </div>
            )}
            <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Belopp</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{booking.amount} kr</dd>
            </div>
            {booking.refundedAmount > 0 && (
                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="text-sm font-medium text-gray-500">Varav återbetalat</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{booking.refundedAmount} kr</dd>
                </div>
            )}
        </dl>
        {booking.email && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                Din biljett har skickats till <span className="font-medium">{booking.email}</span>.
            </div>
        )}
        <div className="mt-6">
            <TicketList tickets={booking.tickets || []} />
        </div>
    </div>
);

export default BookingReceipt;
